import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Clock, Save, MessageSquareHeart, ShieldCheck } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function RetentionCard() {
  const [days, setDays] = useState("");
  const [def, setDef] = useState(5);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/console/trial-retention");
      setDays(String(data.business_days));
      setDef(data.default_business_days);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/console/trial-retention", { business_days: Number(days) });
      setDays(String(data.business_days));
      toast.success("Retention window saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="retention-config">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Clock size={18} className="text-primary" /> Expired-trial data retention
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">
        After a Free Trial ends the entity becomes read-only. Compliance data is permanently
        purged this many <b>business days</b> later (contact info &amp; survey responses are kept).
        Place a per-entity <b>hold</b> below to pause the purge at a customer&apos;s request.
      </p>
      <div className="flex items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Business days</label>
          <Input data-testid="retention-days-input" type="number" min={0} max={120} value={days}
            onChange={(e) => setDays(e.target.value)} className="w-28 rounded-none" />
        </div>
        <Button data-testid="retention-save" onClick={save} disabled={busy} className="rounded-none">
          <Save size={15} className="mr-1" /> {busy ? "Saving…" : "Save"}
        </Button>
        <span className="text-xs text-slate-400 pb-2">Default: {def} business days</span>
      </div>
    </div>
  );
}

function ExpiredTrials() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [dates, setDates] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/console/tenants");
      const trials = (data.tenants || []).filter((t) => t.plan === "free_trial");
      setRows(trials);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleHold = async (t, hold) => {
    setBusy(t.id);
    try {
      await api.post(`/console/tenant/${t.id}/retention-hold`, { hold });
      toast.success(hold ? "Retention hold placed" : "Retention hold lifted");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update hold");
    } finally { setBusy(""); }
  };

  const extend = async (t, body, label) => {
    setBusy(t.id);
    try {
      await api.post(`/console/tenant/${t.id}/extend-trial`, body);
      toast.success(`Trial extended (${label})`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not extend");
    } finally { setBusy(""); }
  };

  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="expired-trials">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <ShieldCheck size={18} className="text-primary" /> Free trials ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No free trials right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
                <th className="py-2 pr-4">Entity</th>
                <th className="py-2 pr-4">Trial ends</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Extend trial</th>
                <th className="py-2 pr-4">Retention hold</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-b last:border-0 align-top" data-testid={`trial-row-${t.id}`}>
                  <td className="py-2 pr-4 font-medium text-slate-900">{t.name}</td>
                  <td className="py-2 pr-4 text-slate-600">{fmt(t.trial_ends_at)}</td>
                  <td className="py-2 pr-4">
                    {t.data_purged ? <span className="text-red-600">Purged</span>
                      : t.locked ? <span className="text-amber-700">Ended</span>
                      : <span className="text-emerald-700">Active</span>}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {[7, 14, 30].map((d) => (
                        <Button key={d} size="sm" variant="outline" className="rounded-none h-7 px-2 text-xs"
                          disabled={busy === t.id} onClick={() => extend(t, { days: d }, `+${d}d`)}
                          data-testid={`extend-${d}-${t.id}`}>+{d}d</Button>
                      ))}
                      <Input type="date" value={dates[t.id] || ""} className="rounded-none h-7 w-36 text-xs"
                        onChange={(e) => setDates((p) => ({ ...p, [t.id]: e.target.value }))}
                        data-testid={`extend-date-${t.id}`} />
                      <Button size="sm" className="rounded-none h-7 px-2 text-xs" disabled={busy === t.id || !dates[t.id]}
                        onClick={() => extend(t, { new_end: dates[t.id] }, dates[t.id])}
                        data-testid={`extend-apply-${t.id}`}>Set</Button>
                    </div>
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`retention-hold-${t.id}`}
                        checked={!!t.retention_hold}
                        disabled={busy === t.id || t.data_purged}
                        onCheckedChange={(v) => toggleHold(t, v)}
                      />
                      <span className="text-xs text-slate-500">{t.retention_hold ? "On hold" : "Auto-purge"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SurveysCard() {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/console/trial-surveys");
      setRows(data.surveys || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="trial-surveys">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <MessageSquareHeart size={18} className="text-primary" /> Trial feedback ({rows.length})
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">No feedback submitted yet.</p>
      ) : (
        <div className="space-y-4">
          {rows.map((s) => (
            <div key={s.id} className="border border-slate-200 p-4 text-sm space-y-2" data-testid={`survey-row-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-slate-900">{s.registration_name}</div>
                <div className="text-xs text-slate-400">{fmt(s.submitted_at)}</div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-slate-700">
                <div><span className="text-slate-400">Satisfaction:</span> {s.responses?.satisfaction || "—"}</div>
                <div><span className="text-slate-400">Onboarding:</span> {s.responses?.onboarding || "—"}</div>
                <div className="sm:col-span-2"><span className="text-slate-400">Wants contact:</span> {s.wants_contact || "—"}</div>
                {s.interested_tiers?.length > 0 && (
                  <div className="sm:col-span-2"><span className="text-slate-400">Interested tiers:</span> {s.interested_tiers.join("; ")}</div>
                )}
                {s.responses?.liked_most && (
                  <div className="sm:col-span-2"><span className="text-slate-400">Liked most:</span> {s.responses.liked_most}</div>
                )}
                {s.responses?.improvements && (
                  <div className="sm:col-span-2"><span className="text-slate-400">Needs before adopting:</span> {s.responses.improvements}</div>
                )}
              </div>
              <div className="border-t border-slate-100 pt-2 text-slate-700">
                <span className="text-slate-400">Contact:</span>{" "}
                {[s.contact?.name, s.contact?.work_email, s.contact?.phone, s.contact?.preferred_time]
                  .filter(Boolean).join(" · ") || "—"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ConsoleTrials() {
  return (
    <div className="space-y-6" data-testid="console-trials">
      <RetentionCard />
      <ExpiredTrials />
      <SurveysCard />
    </div>
  );
}
